// Per-product "Run report" launcher for the brand products table. Fetches
// the product's PDP URLs: one URL starts the async report immediately;
// several open a picker so the user chooses which page to audit; none open
// a URL prompt that creates the PDP URL first (which also enqueues its
// initial score) — so the button always works. Reuses the async
// request-score flow the product settings panel uses (a forced scoring job
// whose stored report backs the row's score).

import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import toast from "react-hot-toast";
import axiosInstant from "../../../../api";
import {
  createProductPdpUrl,
  listProductPdpUrls,
  requestProductPdpUrlScore,
} from "../../../../api/endpoints";
import { ISingleProduct } from "./constants";
import { ScoreChip } from "../../../network/catalogShared";
import { EditUrlDialog } from "../../../network/EditUrlDialog";
import {
  PdpUrlAttempt,
  isAttemptPending,
} from "../../../../components/PdpScoreStatus/PdpScoreStatus";

interface PdpUrlOption {
  id: number;
  pdp_url: string;
  retailer: string | null;
  // Which brand owns the page. A product's pages can span several brands,
  // so the picker has to name them — otherwise two rows read identically.
  brand?: { id: number; name: string } | null;
  latest_score?: { overall_score: number | null } | null;
  latest_attempt?: PdpUrlAttempt | null;
}

interface Props {
  product: ISingleProduct;
  // Brand to stamp a newly-created PDP URL with. The internal grid resolves
  // the product's manufacturer server-side and passes it; the brand table
  // omits it and falls back to reading the product's own brands.
  brandId?: number | null;
  // A specific page to audit (the card's per-URL run icon). Skips the
  // fetch + picker and starts the report for that row straight away.
  pdpUrlId?: number | null;
  onClose: () => void;
  // Fired after a report was successfully started (so the listing can
  // refetch and light the row's "Report running…" state).
  onStarted?: () => void;
}

export function ProductRunReport({ product, brandId: brandIdProp, pdpUrlId, onClose, onStarted }: Props) {
  const [rows, setRows] = useState<PdpUrlOption[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [starting, setStarting] = useState(false);
  const [askUrl, setAskUrl] = useState(false);

  const startReport = async (id: number) => {
    setStarting(true);
    try {
      await axiosInstant.post(
        requestProductPdpUrlScore(product.desireid, id)
      );
      toast.success("Report started — the score updates when it finishes.");
      onStarted?.();
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        toast("A report is already running for this page.");
        // The list didn't know — sync its "Report running…" state.
        onStarted?.();
      } else if (status === 429) {
        toast.error(
          "You've reached the hourly limit for PDP reports. Try again later."
        );
      } else {
        toast.error("Couldn't start the report. Please try again.");
      }
    }
    onClose();
  };

  useEffect(() => {
    let cancelled = false;
    if (pdpUrlId != null) {
      startReport(pdpUrlId);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const res = await axiosInstant.get(
          listProductPdpUrls(product.desireid)
        );
        if (cancelled) return;
        const fetched = (res.data || []) as PdpUrlOption[];
        const idle = fetched.filter((r) => !isAttemptPending(r.latest_attempt));
        if (fetched.length === 0) {
          // No pages yet — ask for one instead of dead-ending.
          setAskUrl(true);
        } else if (idle.length === 0) {
          // Everything is mid-report (the list was stale) — say so.
          toast("A report is already running for this product's page.");
          onStarted?.();
          onClose();
        } else if (fetched.length === 1) {
          startReport(fetched[0].id);
        } else {
          setRows(fetched);
          setSelectedId(idle[0].id);
        }
      } catch {
        if (!cancelled) {
          toast.error("Couldn't load this product's pages. Please try again.");
          onClose();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [product.desireid, pdpUrlId]);

  // No PDP URLs yet: collect one, create the row (its initial score doubles
  // as the report), and confirm. The product's own brand stamps the row —
  // regular brand first, mirroring the settings panel's convention.
  if (askUrl) {
    // A caller-supplied brand wins. The fallback keys off `rmn`, which is
    // only a derived mirror of `brands.plan` and is missing from some
    // product payloads — usable as a last resort, never preferred.
    const brandId =
      brandIdProp ??
      product.brands?.find((b: any) => !b?.rmn)?.id ??
      product.brands?.[0]?.id ??
      null;
    return (
      <EditUrlDialog
        open
        title={`Run PDP Analysis — ${product.title}`}
        label="Product page URL (PDP)"
        helperText="This product has no product page on file yet — paste its URL and we'll audit it. The score lands here when the report finishes."
        initialUrl=""
        allowClear={false}
        saveLabel="Run PDP Analysis"
        savingLabel="Starting…"
        onClose={onClose}
        onSave={async (url) => {
          if (!brandId) {
            throw new Error(
              "No brand on this product yet — set the brand in its settings first."
            );
          }
          try {
            await axiosInstant.post(createProductPdpUrl(product.desireid), {
              pdp_url: url,
              brand_id: brandId,
            });
            toast.success(
              "Report started — the score updates when it finishes."
            );
            onStarted?.();
          } catch (e: any) {
            if (e?.response?.status === 429) {
              throw new Error(
                "You've reached the hourly limit for PDP reports. Try again later."
              );
            }
            throw new Error(
              "Couldn't add that page to the product. Please try again."
            );
          }
        }}
      />
    );
  }

  // Single-URL products never render UI — the report starts straight away.
  if (!rows) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Run PDP Analysis — {product.title}
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="text-[13px] text-text-2 mb-3">
          This product has several product pages — pick the one to audit.
        </p>
        <div className="flex flex-col border border-border-token rounded-xl overflow-hidden mb-5">
          {rows.map((row) => {
            const running = isAttemptPending(row.latest_attempt);
            return (
              <label
                key={row.id}
                className={`flex items-center gap-3 px-3 py-2.5 border-b border-border-token last:border-0 ${
                  running
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:bg-surface-2"
                }`}
              >
                <input
                  type="radio"
                  name="run-report-pdp"
                  checked={selectedId === row.id}
                  disabled={running}
                  onChange={() => setSelectedId(row.id)}
                  className="shrink-0"
                />
                <span
                  className="flex-1 min-w-0 text-[13px] text-text-1 truncate"
                  title={row.pdp_url}
                >
                  {row.pdp_url.replace(/^https?:\/\/(www\.)?/, "")}
                </span>
                {running && (
                  <span className="text-[11px] font-semibold text-accent-text bg-tint rounded-full px-2.5 py-1 shrink-0">
                    Report running…
                  </span>
                )}
                {row.brand?.name && (
                  <span
                    className="text-[11px] text-muted shrink-0"
                    title={`This page belongs to ${row.brand.name}`}
                  >
                    {row.brand.name}
                  </span>
                )}
                {row.retailer && (
                  <span className="text-[11px] text-muted shrink-0">
                    {row.retailer}
                  </span>
                )}
                <ScoreChip
                  score={row.latest_score?.overall_score}
                  title="Latest score for this page"
                />
              </label>
            );
          })}
        </div>
        <button
          type="button"
          disabled={starting || !selectedId}
          onClick={() => selectedId && startReport(selectedId)}
          className="w-full py-3 rounded-xl bg-primary text-white text-[14px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {starting ? "Starting analysis…" : "Run PDP Analysis"}
        </button>
        <p className="text-[12px] text-muted mt-2.5 text-center">
          Reports run in the background and usually take a few minutes.
        </p>
      </DialogContent>
    </Dialog>
  );
}
